import { DeleteOutlined } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Collapse,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getS3Url } from 'src/constants';
import VehicleSelectField from './VehicleSelectField';

const DriverItem = ({ driver }) => {
  const { t } = useTranslation('translation');
  return (
    <Box
      sx={{
        opacity: driver?.is_available ? 1 : 0.5,
        overflow: 'hidden',
      }}
    >
      <Typography
        noWrap
        textOverflow="ellipsis"
        overflow="hidden"
        fontSize="15px"
        fontWeight={600}
      >
        {driver?.name || t('common.na')} &nbsp;
        {driver?.name &&
          !driver?.is_available &&
          `(${t('common.unavailable')})`}
      </Typography>
      <Typography
        fontSize="12px"
        textTransform="uppercase"
        color="text.secondary"
        variant="body2"
        noWrap
      >
        {driver?.name
          ? `${driver.user_formatted_address || driver.email}`
          : t('common.na')}
      </Typography>
    </Box>
  );
};

const DriverSelector = ({
  drivers = [],
  vehicles = [],
  error,
  errors,
  driversValue = [],
  onChange,
  sx = {},
}) => {
  // Extract driver error from errors object if error prop is not provided
  const driverError = error || errors?.drivers?.message;
  const [showMaxDriversError, setShowMaxDriversError] = useState(false);
  const { t } = useTranslation('translation');
  useEffect(() => {
    if ((driversValue?.length ?? 0) < 3) {
      setShowMaxDriversError(false);
    }
  }, [driversValue]);
  return (
    <Box sx={sx}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
        }}
      >
        {t('donations.choose-a-driver-and-a-vehicle')}
      </Typography>
      <Stack
        my={2}
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={2}
      >
        <FormControl fullWidth>
          <InputLabel shrink>{t('donations.driver')}</InputLabel>
          <Select
            startAdornment={
              <Avatar
                sx={{
                  width: 35,
                  height: 35,
                  mr: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
                src={null}
              />
            }
            label={t('donations.driver')}
            value=""
            onChange={(e) => {
              const driverId = e.target.value;
              if (driverId && driversValue.length < 3) {
                // Auto-add driver when selected
                onChange([
                  ...driversValue,
                  {
                    user_id: driverId,
                    vehicle_id: null,
                  },
                ]);
              }
            }}
            onOpen={() => {
              if (driversValue.length >= 3) {
                setShowMaxDriversError(true);
              }
            }}
            displayEmpty
            disabled={driversValue.length >= 3}
            renderValue={() => t('donations.select-a-driver-to-add')}
            sx={{
              '& .MuiSelect-select': {
                py: '14px !important',
              },
            }}
          >
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              value=""
            >
              {t('donations.unassigned')}
            </MenuItem>
            {drivers
              .filter(
                (d) => !driversValue.map((dv) => dv.user_id).includes(d.id)
              )
              .map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  <DriverItem driver={driver} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Stack>

      {driverError || showMaxDriversError ? (
        <FormHelperText error sx={{ mt: 0.5, mx: 0 }}>
          {driverError || t('donations.max-3-drivers')}
        </FormHelperText>
      ) : null}

      <Box mt={1}>
        <Typography variant="body1">{t('donations.added-drivers')}</Typography>
        {!driversValue.length && (
          <Typography variant="body2" color="text.secondary">
            {t('common.none')}
          </Typography>
        )}
      </Box>

      <Collapse in={!!driversValue.length}>
        <Box sx={{ mt: 2 }}>
          <List disablePadding>
            {driversValue.map((driver) => {
              const driverData = drivers.find((d) => d.id === driver.user_id);

              const handleVehicleChange = (vehicleId) => {
                onChange(
                  driversValue.map((d) =>
                    d.user_id === driver.user_id
                      ? { ...d, vehicle_id: vehicleId || null }
                      : d
                  )
                );
              };

              return (
                <ListItem
                  key={driver.user_id}
                  sx={{
                    px: 0,
                    py: 1,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={t('common.remove')}
                      onClick={() => {
                        onChange(
                          driversValue.filter(
                            (d) => d.user_id !== driver.user_id
                          )
                        );
                      }}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{ width: 32, height: 32 }}
                      src={
                        driverData?.image ? getS3Url(driverData.image) : null
                      }
                    />
                  </ListItemAvatar>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      fontSize="14px"
                      fontWeight={600}
                      noWrap
                      sx={{ minWidth: 100 }}
                    >
                      {driverData?.name || driver.user_name || t('common.na')}
                    </Typography>
                    <VehicleSelectField
                      value={driver.vehicle_id || ''}
                      onChange={handleVehicleChange}
                      vehicles={vehicles}
                      compact
                    />
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </Box>
  );
};

export default DriverSelector;
